import apiService from "../../api/api.service";
import {
  GetCollectionsResponse,
  GetCollectionResponse,
  GetCollectionFormDataResponse,
  CreateCollectionRequest,
  CreateCollectionResponse,
  UpdateCollectionRequest,
  UpdateCollectionResponse,
  DeleteCollectionResponse,
  GetFollowedCollectionsResponse,
  GetCollectionsSummaryResponse,
  AddItemToCollectionRequest,
  AddItemResponse,
  RemoveItemResponse,
  RemoveItemFromCollectionRequest,
  ToggleFollowResponse,
} from "./collection.types";
import { FilterRequest } from "../filters/filters.types";

export interface FetchCollectionsParams {
  search?: string;
}

export const fetchCollections = async (
  params?: FetchCollectionsParams,
): Promise<GetCollectionsResponse> => {
  return apiService.get<GetCollectionsResponse>("/customers/collections", {
    params,
  });
};

export const fetchCollection = async (
  idOrUuid: string | number,
  page?: number,
  filters?: Partial<FilterRequest>,
): Promise<GetCollectionResponse> => {
  const { section_id, seque, ...rest } = filters ?? {};
  const filtersClean = Object.fromEntries(
    Object.entries(rest).filter(([, v]) => v !== undefined),
  );
  const params = {
    ...(page ? { page } : {}),
    ...(section_id !== undefined ? { section_id } : {}),
    ...(seque !== undefined ? { seque } : {}),
    ...(Object.keys(filtersClean).length ? { filters: filtersClean } : {}),
  };
  return apiService.get<GetCollectionResponse>(
    `/customers/collections/${idOrUuid}`,
    Object.keys(params).length ? { params } : undefined,
  );
};

export const fetchCollectionFormData = async (
  id?: number,
): Promise<GetCollectionFormDataResponse> => {
  const endpoint = id
    ? `/customers/collections/update-or-create/${id}`
    : "/customers/collections/update-or-create";
  return apiService.get<GetCollectionFormDataResponse>(endpoint);
};

export const createCollection = async (
  data: CreateCollectionRequest,
): Promise<CreateCollectionResponse> => {
  return apiService.post<CreateCollectionResponse, CreateCollectionRequest>(
    "/customers/collections",
    data,
  );
};

export const updateCollection = async (
  collectionId: number,
  data: UpdateCollectionRequest,
): Promise<UpdateCollectionResponse> => {
  return apiService.put<UpdateCollectionResponse, UpdateCollectionRequest>(
    `/customers/collections/${collectionId}`,
    data,
  );
};

export const deleteCollection = async (
  collectionId: number,
): Promise<DeleteCollectionResponse> => {
  return apiService.delete<DeleteCollectionResponse>(
    `/customers/collections/${collectionId}`,
  );
};

export const fetchFollowedCollections = async (
  params?: FetchCollectionsParams,
): Promise<GetFollowedCollectionsResponse> => {
  return apiService.get<GetFollowedCollectionsResponse>(
    "/customers/collections/followed",
    { params },
  );
};

export const fetchCollectionsSummary = async (
  params?: FetchCollectionsParams,
): Promise<GetCollectionsSummaryResponse> => {
  return apiService.get<GetCollectionsSummaryResponse>(
    "/customers/collections/summary",
    { params },
  );
};

export const addItemToCollection = async (
  collectionId: number,
  data: AddItemToCollectionRequest,
): Promise<AddItemResponse> => {
  return apiService.post<AddItemResponse, AddItemToCollectionRequest>(
    `/customers/collections/${collectionId}/items`,
    data,
  );
};

export const removeItemFromCollection = async (
  collectionId: number,
  data: RemoveItemFromCollectionRequest,
): Promise<RemoveItemResponse> => {
  return apiService.post<RemoveItemResponse, RemoveItemFromCollectionRequest>(
    `/customers/collections/${collectionId}/items/remove`,
    data,
  );
};

export const toggleFollowCollection = async (
  collectionId: number,
): Promise<ToggleFollowResponse> => {
  return apiService.post<ToggleFollowResponse>(
    `/customers/collections/${collectionId}/toggle-follow`,
  );
};
